import { useEffect, type RefObject } from "react";
import type { HubConnection } from "@microsoft/signalr";
import { useLockStore } from "../../store/lockStore";
import { lockApi } from "../../api/lockApi";
import { asError, eventBus } from "@optimyl/events";
import { REQUEST_TIMEOUT_SECONDS } from "./constants";
import type {
  AccessRequestedPayload,
  LockReleasedPayload,
  RequestResolvedPayload,
} from "../../types/lock";

/**
 * Registers the three SignalR event handlers for the form-lock hub:
 *
 *  - `accessRequested` — a non-holder is asking the lock holder for access.
 *    Updates `incomingRequest` (drives the holder's yellow banner) and
 *    resets the 30s countdown.
 *  - `requestResolved` — the request was granted, denied, or cancelled.
 *    Branches by current `lockStatus`:
 *      * Holder → granted → "removed".
 *      * Requester → granted → `startGrantedTransition` (idempotent).
 *      * Requester → denied → back to "locked-by-other".
 *      * Anyone on lock screen → granted-to-us via missed-requesting path →
 *        defence-in-depth acquire + reload.
 *  - `lockReleased` — the lock was freed (clean release or transfer without
 *    a pending request). Anyone on the lock screen tries to acquire.
 *
 * Cancelled isn't surfaced as `outgoingRequestStatus` — the requester
 * initiated the cancel and already cleared their own state; only the holder
 * receives this branch as a banner-dismissal signal.
 */
export function useLockSignalREvents(
  connection: HubConnection | null,
  formId: string | null,
  formIdRef: RefObject<string | null>,
  sessionIdRef: RefObject<string>,
  onLockGrantedRef: RefObject<(() => Promise<void>) | undefined>,
  startGrantedTransition: () => void,
): void {
  const setLockStatus = useLockStore((s) => s.setLockStatus);
  const setIncomingRequest = useLockStore((s) => s.setIncomingRequest);
  const setCountdown = useLockStore((s) => s.setCountdown);
  const setRequestInProgress = useLockStore((s) => s.setRequestInProgress);
  const setOutgoingStatus = useLockStore((s) => s.setOutgoingStatus);

  useEffect(() => {
    if (!connection || !formId) return;

    const onAccessRequested = (payload: AccessRequestedPayload) => {
      if (payload.formId !== formIdRef.current) return;
      setIncomingRequest({ email: payload.requestedBy, since: Date.now() });
      setRequestInProgress(payload.requestedBy);
      setCountdown(REQUEST_TIMEOUT_SECONDS);
    };

    const onRequestResolved = (payload: RequestResolvedPayload) => {
      if (payload.formId !== formIdRef.current) return;

      // Snapshot lockStatus BEFORE clearing transient state so we can branch on it.
      const state = useLockStore.getState();
      const wasRequester = state.lockStatus === "requesting";

      setIncomingRequest(null);
      setRequestInProgress(null);
      setCountdown(null);
      // Scope `outgoingRequestStatus` to the original requester only. Without this
      // guard, third-party lock-screen viewers (who never requested) would get
      // their outgoingRequestStatus set to "Granted" or "Denied" — and LockScreen
      // reads `outgoingRequestStatus === "Denied"` to render the "still actively
      // editing" message. That would surface the denial UX to users who weren't
      // involved in the request flow.
      if (wasRequester && payload.status !== "Cancelled") {
        setOutgoingStatus(payload.status);
      }

      // Holder + granted → we're being removed.
      if (state.lockStatus === "locked" && payload.status === "Granted" && payload.newHolder !== null) {
        setLockStatus("removed", payload.newHolder);
      }
      // Requester + granted → take the lock with the 2s "Access Granted" overlay.
      if (state.lockStatus === "requesting" && payload.status === "Granted" && formIdRef.current) {
        startGrantedTransition();
      }
      // locked-by-other + granted: two sub-cases. (1) Defence in depth: we missed
      // the requesting state (e.g. we cancelled locally then the holder ignored
      // and the server auto-granted to us anyway) but the server says we're the
      // new holder — acquire so the UI follows the server. (2) We're NOT the new
      // holder, just a third-party viewer of someone else's grant — update the
      // displayed lockedBy to the new holder so the lock screen reflects reality
      // immediately, instead of waiting for the next useLockScreenPoll cycle.
      if (state.lockStatus === "locked-by-other" && payload.status === "Granted" && formIdRef.current) {
        const reloadPromise = onLockGrantedRef.current ? onLockGrantedRef.current().catch((err) => {
          eventBus.publish("error", { error: asError(err), context: "useLockSignalREvents.reloadAfterGrantFromLockScreen" });
        }) : Promise.resolve();

        lockApi.acquireLock(formIdRef.current, sessionIdRef.current)
          .then(async (r) => {
            if (r.acquired) {
              await reloadPromise;
              setLockStatus("locked");
            } else if (payload.newHolder) {
              // Sub-case 2: we're a third-party viewer. Update the displayed holder.
              setLockStatus("locked-by-other", payload.newHolder);
            }
          })
          .catch((err) => {
            eventBus.publish("error", { error: asError(err), context: "useLockSignalREvents.acquireAfterGrantFromLockScreen" });
          });
      }
      // Requester + denied → back to the lock screen.
      if (state.lockStatus === "requesting" && payload.status === "Denied") {
        setLockStatus("locked-by-other", state.lockedBy);
      }
    };

    const onLockReleased = (payload: LockReleasedPayload) => {
      if (payload.formId !== formIdRef.current) return;
      const state = useLockStore.getState();
      if ((state.lockStatus === "locked-by-other" || state.lockStatus === "requesting") && formIdRef.current) {
        // Reload form data in parallel with the acquire. The previous holder may
        // have submitted, saved, or otherwise changed state we need to see —
        // without the refresh, we'd flip the new holder to "locked" with stale
        // local form data (notably: a submitted form would still render its
        // draft view because `formStatus` wouldn't have updated). FormPage's
        // render branches on `formStatus === "submitted"` first, so awaiting
        // the reload before setting "locked" routes the new holder to
        // StepThankYou cleanly when applicable.
        const reloadPromise = onLockGrantedRef.current
          ? onLockGrantedRef.current().catch((err) => {
              eventBus.publish("error", { error: asError(err), context: "useLockSignalREvents.reloadAfterRelease" });
            })
          : Promise.resolve();
        lockApi.acquireLock(formIdRef.current, sessionIdRef.current)
          .then(async (r) => {
            if (r.acquired) {
              await reloadPromise;
              setLockStatus("locked");
            } else if (r.lockedBy) {
              // Lost the race to another lock-screen viewer who got there first.
              // Update the displayed holder so the lock screen reflects who actually
              // has it now. (If the lock was released because the form was submitted,
              // the parallel reloadPromise sets formStatus="submitted" and FormPage's
              // render branches to StepThankYou regardless of this lockedBy value.)
              setLockStatus("locked-by-other", r.lockedBy);
            }
          })
          .catch((err) => {
            eventBus.publish("error", { error: asError(err), context: "useLockSignalREvents.acquireAfterRelease" });
          });
      }
    };

    connection.on("accessRequested", onAccessRequested);
    connection.on("requestResolved", onRequestResolved);
    connection.on("lockReleased", onLockReleased);

    return () => {
      connection.off("accessRequested", onAccessRequested);
      connection.off("requestResolved", onRequestResolved);
      connection.off("lockReleased", onLockReleased);
    };
  }, [connection, formId, formIdRef, sessionIdRef, onLockGrantedRef, setIncomingRequest, setRequestInProgress, setCountdown, setOutgoingStatus, setLockStatus, startGrantedTransition]);
}
